import { useEffect, useState } from "react"
import { Menu, Languages } from "lucide-react"
import { Button } from "@workspace/ui/components/button"
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@workspace/ui/components/sheet"
import { Separator } from "@workspace/ui/components/separator"
import { cn } from "@workspace/ui/lib/utils"
import { useI18n } from "@/lib/i18n"
import { Logo } from "./logo"

export function Nav() {
  const { t, toggle, lang } = useI18n()
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  const links = [
    { n: "01", href: "/#work", label: t.nav.work },
    { n: "02", href: "/#products", label: t.nav.products },
    { n: "03", href: "/#studio", label: t.nav.studio },
    { n: "04", href: "/#principles", label: t.nav.principles },
    { n: "05", href: "/#contact", label: t.nav.contact },
  ]

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-300",
        scrolled
          ? "border-b border-border bg-background/80 backdrop-blur-md"
          : "border-b border-transparent",
      )}
    >
      <nav className="mx-auto flex h-20 max-w-[1400px] items-center justify-between px-6 md:px-14 xl:px-20">
        <Logo />

        {/* Desktop links — labels only; section numbers live on each section's header */}
        <ul className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                className="font-mono text-[12px] tracking-[0.04em] text-foreground/70 transition-colors hover:text-primary"
              >
                <span className="link-underline">{l.label}</span>
              </a>
            </li>
          ))}
        </ul>

        {/* Actions — visible from tablet up; nav links remain desktop-only above */}
        <div className="hidden items-center gap-3 md:flex">
          <Button
            variant="outline"
            size="md"
            onClick={toggle}
            aria-label={`Switch language · current: ${lang}`}
            className="rounded-none border-border font-mono text-[11px] uppercase tracking-[0.18em] text-foreground/75 hover:border-primary hover:text-primary"
          >
            <Languages data-icon="inline-start" />
            {t.nav.language}
          </Button>
          <Button
            asChild
            variant="outline"
            size="md"
            className="rounded-none border-primary/50 font-mono text-[11px] uppercase tracking-[0.16em] text-primary hover:bg-primary/10 hover:text-primary hover:border-primary"
          >
            <a href="/#products">{t.nav.cta}</a>
          </Button>
        </div>

        {/* Mobile menu trigger */}
        <Sheet>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="icon-lg"
              aria-label="Open menu"
              className="rounded-none border-border text-foreground/70 md:hidden"
            >
              <Menu />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="border-border bg-background px-0">
            <SheetHeader className="px-8 pt-8">
              <SheetTitle className="font-mono text-[10px] uppercase tracking-[0.24em] text-foreground/70">
                Menu
              </SheetTitle>
            </SheetHeader>
            <Separator className="my-2" />
            <ul className="flex flex-col gap-1 px-8 pb-6">
              {links.map((l) => (
                <li key={l.href}>
                  <SheetClose asChild>
                    <a
                      href={l.href}
                      className="block py-3 text-2xl font-semibold tracking-tight text-foreground transition-colors hover:text-primary"
                    >
                      <span className="mr-3 font-mono text-xs text-primary">
                        {l.n}.
                      </span>
                      {l.label}
                    </a>
                  </SheetClose>
                </li>
              ))}
            </ul>
            <Separator />
            <div className="flex items-center justify-between gap-3 px-8 py-5">
              <Button
                variant="outline"
                size="md"
                onClick={toggle}
                aria-label={`Switch language · current: ${lang}`}
                className="rounded-none border-border font-mono text-[11px] uppercase tracking-[0.18em] text-foreground/75"
              >
                <Languages data-icon="inline-start" />
                {t.nav.language}
              </Button>
              <SheetClose asChild>
                <Button
                  asChild
                  variant="outline"
                  size="md"
                  className="rounded-none border-primary/50 font-mono text-[11px] uppercase tracking-[0.16em] text-primary"
                >
                  <a href="/#products">{t.nav.cta}</a>
                </Button>
              </SheetClose>
            </div>
          </SheetContent>
        </Sheet>
      </nav>
    </header>
  )
}
